<template>
    <div class="fixed-footer" :class="[footerClass, { 'rs-mobile-footer': useModernMenu }]">
        <ul v-if="useModernMenu" :data-menu-count="centeredItems.length" class="rs-mobile-footer-list" :style="{ gridTemplateColumns: 'repeat(' + centeredItems.length + ', minmax(0, 1fr))' }">
            <li v-for="(item, index) in centeredItems" :key="item.text"
                class="rs-mobile-item"
                :class="{ active: isActive(url(item)), 'rs-mobile-dashboard': useCenteredMenu && index === middleIndex }">
                <router-link :to="url(item)" :title="item.text" :aria-label="item.text" class="rs-mobile-link"
                    :aria-current="isActive(url(item)) ? 'page' : undefined">
                    <span class="lucide-menu-icon" aria-hidden="true" v-html="useCenteredMenu && index === middleIndex ? dashboardLogo : icon(item)"></span>
                    <span v-if="!useCenteredMenu || index !== middleIndex" class="rs-mobile-label">{{ item.text }}</span>
                </router-link>
            </li>
        </ul>
        <ul v-else
            class="list-items"
            :class="[
                menuCount == 3 ? 'menu-count-3' : '',
                menuCount == 5 ? 'menu-count-5' : ''
            ]"
        >
            <slot></slot>
        </ul>
    </div>
</template>

<script>
import { computed, reactive, toRefs } from "vue";
import { C } from "@/helpers/global";
import { Links } from "@/helpers/links";

const icons = {
    'icon-dashboard': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>',
    'icon-user': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>',
    'icon-user-graduate': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m22 10-10-5L2 10l10 5 10-5ZM6 12v5c3 3 9 3 12 0v-5M22 10v6"/></svg>',
    'icon-week-plan': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3 10h18"/><rect x="3" y="4" width="18" height="18" rx="2"/></svg>',
    'icon-daily-plan': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3 10h18M8 14h2M14 14h2M8 18h2"/><rect x="3" y="4" width="18" height="18" rx="2"/></svg>',
    'icon-books-list': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 10 5-10 5L2 8l10-5ZM2 12l10 5 10-5M2 16l10 5 10-5"/></svg>',
    'icon-chart-pie': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18M8 16V9M13 16V5M18 16v-5"/></svg>',
    'icon-bookmark': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',
    'icon-folderbag': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 2h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2Z"/></svg>',
    'icon-exam': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M8 13h8M8 17h8"/></svg>',
    'icon-file-circle-question': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/></svg>',
    'icon-chevron-left': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>',
    'icon-book-open-reader': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14M3 3h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5v15h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3H3Z"/></svg>',
    'icon-meeting': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M8 2v4M16 2v4M3 10h18m-12 6 2 2 4-4"/></svg>',
    'icon-calendar-days-regular': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4M16 2v4M3 10h18"/><rect x="3" y="4" width="18" height="18" rx="2"/></svg>',
    'icon-book': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 7v14M3 3h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5v15h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3H3Z"/></svg>',
    'icon-chemistry-lesson': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M8 13h8M8 17h8"/></svg>',
    'icon-layer-group': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3 10 5-10 5L2 8l10-5ZM2 12l10 5 10-5M2 16l10 5 10-5"/></svg>',
    'icon-video-arrow-up': '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m10 9 5 3-5 3Z"/></svg>',
};
const dashboardLogo = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37.69 54"> <path fill="currentColor" d="M16.98,28.88c-.31-.04-.62-.12-.92-.23-1.26-.37-2.43-.99-3.43-1.83-.26-.21-.51-.44-.74-.69-1.87-1.89-2.92-4.44-2.92-7.1s1.05-5.21,2.92-7.1c1.87-1.88,4.42-2.94,7.08-2.95,1.32,0,2.62.25,3.83.75,1.22.5,2.32,1.24,3.25,2.16.93.93,1.67,2.03,2.18,3.25.51,1.21.77,2.52.77,3.83,0,1.32-.25,2.62-.75,3.83-.5,1.22-1.24,2.32-2.16,3.25-1.17,1.19-2.63,2.06-4.23,2.52-.62.17-1.25.28-1.89.34-.99.15-2,.13-2.98-.06ZM9.03,29.28l-1.55-1.54c-.06-.06-.11-.11-.11-.17-1.18-1.72-1.98-3.67-2.37-5.72-.38-2.05-.34-4.16.13-6.19.03-.23.09-.47.17-.69.06-.17.11-.29.17-.46.06-.06.06-.17.11-.23.51-1.38,1.23-2.67,2.12-3.83l-.06-.06-2.12-2.12c-.09-.09-.19-.17-.31-.21-.12-.05-.25-.06-.37-.05-.13.01-.25.05-.36.12-.11.07-.2.16-.27.26-2.07,2.86-3.26,6.26-3.43,9.79v1.66c.04,1.18.19,2.34.46,3.49v.11c.07.16.1.34.11.51l.17.51v.11c.18.65.41,1.28.69,1.89,0,0,0,.02,0,.02,0,0,0,.01.01.02,0,0,.01,0,.02.01,0,0,.01,0,.02,0,.11.29.23.51.34.8.12.2.21.41.29.63L.04,35.92c-.06.18-.06.38,0,.57.07.18.19.34.35.44.16.1.36.15.55.14.19-.02.38-.1.52-.23l.57-.57,4.12-4.12,2.75-2.75.11-.11ZM19.67,53.55c-.08.14-.19.25-.33.33-.14.08-.29.12-.45.12s-.31-.04-.45-.12c-.14-.08-.25-.19-.33-.33l-10.82-20.32-.06-.06,2.29-2.29c.14-.14.33-.23.52-.25.2-.02.4.03.56.14.46.34.97.63,1.43.97.06,0,.06.06.11.06.55.3,1.12.54,1.72.74.15.07.3.12.46.17.06,0,.17.06.23.06.97.3,1.97.51,2.98.63,1.69.15,3.39.01,5.04-.4.23-.06.4-.11.63-.17.59-.18,1.16-.41,1.72-.69.11-.06.17-.06.23-.11.11-.06.17-.06.29-.11.55-.29,1.09-.62,1.6-.98l.69-.51.86-.86.06-.06.34-.34,2.75,2.75,4.75,4.75c.13.14.21.32.23.52.02.19-.03.39-.14.55-.1.16-.26.29-.44.35-.18.07-.38.07-.57.01l-5.38-1.89c-.19-.07-.4-.07-.59,0-.19.07-.35.22-.44.4l-9.5,16.94ZM28.71,8.74l2.75-2.75,2.29-2.29,2.46-2.46c.14-.14.32-.22.52-.24.19-.02.39.03.56.13.16.11.29.26.35.45.06.18.07.39,0,.57l-2.8,7.95c.12.2.21.41.29.63.11.29.23.51.34.8,0,0,0,.02,0,.02s0,.01.01.02c0,0,.01,0,.02.01s.01,0,.02,0c.29.63.46,1.26.69,1.89v.11l.17.51c.07.16.1.34.11.51v.11c.67,2.73.69,5.58.06,8.32-.63,2.74-1.88,5.3-3.67,7.47l-.11-.11-2.29-2.29c-.15-.15-.23-.35-.24-.56-.01-.21.06-.42.19-.58.68-1.02,1.26-2.12,1.72-3.26.06-.06.06-.17.11-.23.06-.17.11-.29.17-.46.3-.95.51-1.93.63-2.92.05-.42.07-.84.06-1.26v-.97c0-.17-.06-.34-.06-.51-.36-3.18-1.79-6.15-4.06-8.41l-.23-.23h-.06ZM4.79,5.08L1.19,1.47c-.13-.14-.21-.32-.23-.52-.02-.19.03-.39.14-.55.1-.16.26-.29.44-.35.18-.07.38-.07.57,0l8.01,2.8c.18-.11.38-.2.57-.29l.69-.34c.06,0,.11-.06.17-.06.63-.29,1.28-.52,1.95-.69h.11c.16-.07.34-.1.51-.11.16-.07.34-.1.51-.11h.11c1.15-.26,2.32-.42,3.49-.45h.46c1.45,0,2.89.17,4.29.51h.06c.23.06.46.11.74.17.11.06.29.06.4.11.11.06.29.06.4.11.23.06.46.17.74.23h.06c1.53.59,2.97,1.38,4.28,2.34.1.08.18.17.24.29.06.11.09.23.09.36,0,.12-.02.25-.06.37-.05.12-.12.22-.21.31l-2.11,2.12-.06.06c-2.48-1.91-5.52-2.93-8.64-2.92-1.86-.01-3.71.35-5.43,1.07-1.72.72-3.28,1.77-4.58,3.11l-.06.06-.11-.11-2.8-2.69-1.14-1.2Z" /> </svg>';

export default {
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        isActive: {
            type: Function,
            default: () => false,
        },
        menuCount: {
            type: Number,
            default: 3,
        },
        footerClass: {
            type: String,
            default: "",
        },
    },
    
    setup(props) {
        const visibleItems = computed(() => props.items.filter(Boolean));
        const useModernMenu = computed(() => [2, 3, 4, 5].includes(visibleItems.value.length));
        const useCenteredMenu = computed(() => [3, 5].includes(visibleItems.value.length));
        const middleIndex = computed(() => Math.floor(visibleItems.value.length / 2));
        const centeredItems = computed(() => {
            const items = [...visibleItems.value];
            let index = items.findIndex(item => item.link === Links.Dashboard || item.kind === 'dashboard');
            if (index < 0) {
                index = items.findIndex(item => item.isMain);
            }
            if (index >= 0) {
                const [main] = items.splice(index, 1);
                items.splice(useCenteredMenu.value ? middleIndex.value : 0, 0, main);
            }
            return items;
        });
        const url = item => item.path || Links.Url(item.link, item.args);
        const icon = item => {
            if (item.link === Links.Dashboard || item.kind === 'dashboard') {
                return icons['icon-dashboard'];
            }
            const key = { appointments: 'icon-meeting', documents: 'icon-books-list', profile: 'icon-user' }[item.key];
            return icons[item.icon || key] || item.iconSvg || item.svg || icons['icon-dashboard'];
        };

        const e = reactive({
            sidebarStatus: C.Bool(localStorage.getItem("left-sidebar")),

                changeSideBar() {
                    e.sidebarStatus = !e.sidebarStatus;
                localStorage.setItem("left-sidebar", e.sidebarStatus ? "1" : "0");
                },
            });

        return { ...toRefs(e), useModernMenu, useCenteredMenu, centeredItems, middleIndex, url, icon, dashboardLogo };
        },
    };
</script>
<style scoped>
.fixed-footer.rs-mobile-footer {
    height: calc(68px + env(safe-area-inset-bottom, 0px));
    padding: 0 10px env(safe-area-inset-bottom, 0px);
    background: #fff;
    border-radius: 28px 28px 0 0;
    filter: none;
}

.rs-mobile-footer-list {
    display: grid;
    align-items: start;
    height: 68px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.rs-mobile-item {
    display: flex;
    justify-content: center;
    min-width: 0;
    height: 68px;
}

.rs-mobile-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
    height: 100%;
    padding: 7px 2px 9px;
    color: #8f56e8;
    text-decoration: none;
}

.lucide-menu-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 34px;
    width: 38px;
    height: 34px;
    border-radius: 16px;
}

.lucide-menu-icon :deep(svg) {
    display: block;
    width: 26px;
    height: 26px;
}

.rs-mobile-label {
    max-width: 100%;
    color: #191622;
    font-size: 10px;
    line-height: 12px;
    font-weight: 500;
    text-align: center;
    white-space: nowrap;
}

.rs-mobile-item.active:not(.rs-mobile-dashboard) .lucide-menu-icon {
    background: #f1e8ff;
}

.rs-mobile-item.active .rs-mobile-label {
    color: #7132e8;
    font-weight: 600;
}

.rs-mobile-footer-list[data-menu-count="2"] .rs-mobile-link {
    flex-direction: row;
    justify-content: center;
    gap: 8px;
    padding: 10px 4px;
}

.rs-mobile-footer-list[data-menu-count="2"] .rs-mobile-label {
    font-size: 13px;
    line-height: 18px;
}

.rs-mobile-footer-list[data-menu-count="2"] .lucide-menu-icon {
    flex: 0 0 34px;
    width: 34px;
}

.rs-mobile-dashboard .rs-mobile-link {
    justify-content: flex-start;
    padding: 0 2px;
}

.rs-mobile-dashboard .lucide-menu-icon {
    flex: 0 0 82px;
    width: 82px;
    height: 82px;
    margin-top: -28px;
    border-radius: 50%;
    color: #fff;
    background:
        radial-gradient(circle at 30% 24%, rgba(255, 255, 255, .42), transparent 28%),
        linear-gradient(135deg, #7b28d8 0%, #9b49d0 40%, #2fa7df 68%, #24c4a4 100%);
    box-shadow: 0 12px 28px rgba(123, 40, 216, .24);
}

.rs-mobile-dashboard .lucide-menu-icon :deep(svg) {
    width: 44px;
    height: 54px;
}

.rs-mobile-link:focus-visible {
    outline: 2px solid #c8adef;
    outline-offset: -2px;
    border-radius: 16px;
}

@media (max-width: 360px) {
    .rs-mobile-label {
        font-size: 9px;
    }
}
</style>
